/*
 * Responsive image component. Given a base `/images/foo.webp` path plus its
 * intrinsic dimensions, it emits a `srcset` referencing the generated
 * `foo-{width}w.webp` variants (see scripts/gen-images.mjs) so the browser
 * downloads a correctly-sized image per viewport.
 *
 * - width/height are always set to reserve layout space (prevents CLS).
 * - Below-the-fold images default to loading="lazy" + decoding="async".
 * - The LCP image passes `priority` to get loading="eager" + fetchpriority="high".
 */
import * as React from "react";

const VARIANT_WIDTHS = [640, 1024, 1536];

type ImgProps = {
  /** Base image path, e.g. "/images/hero-bg-XXX.webp" */
  src: string;
  /** Intrinsic pixel dimensions of the source image. */
  width: number;
  height: number;
  alt: string;
  sizes?: string;
  /** LCP / above-the-fold: eager load with high fetch priority. */
  priority?: boolean;
  /** When true, omits width/height attrs from the rendered img. Use for
   *  absolutely-positioned cover images where the container sets size. */
  omitIntrinsicSize?: boolean;
  className?: string;
} & Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "width" | "height" | "alt" | "sizes" | "loading"
>;

export function Img({
  src,
  width,
  height,
  alt,
  sizes = "100vw",
  priority = false,
  omitIntrinsicSize = false,
  className,
  ...rest
}: ImgProps) {
  // Variants live in /images/responsive/ as `{name}-{width}w.webp`
  // (originals are left untouched in /images/ to avoid OneDrive write locks).
  const name = src.replace(/^.*\//, "").replace(/\.webp$/, "");
  const base = `/images/responsive/${name}`;
  const widths = [...VARIANT_WIDTHS, width]
    .filter((w, i, a) => w <= width && a.indexOf(w) === i)
    .sort((a, b) => a - b);
  const srcSet = widths.map((w) => `${base}-${w}w.webp ${w}w`).join(", ");
  const largest = `${base}-${widths[widths.length - 1]}w.webp`;

  return (
    <img
      src={largest}
      srcSet={srcSet}
      sizes={sizes}
      {...(!omitIntrinsicSize && { width, height })}
      alt={alt}
      className={className}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "async" : "async"}
      fetchPriority={priority ? "high" : "auto"}
      {...rest}
    />
  );
}

export default Img;
